import qs from 'qs';
import React from 'react';
import BrowserProtocol from 'farce/lib/BrowserProtocol';
import createQueryMiddleware from 'farce/lib/createQueryMiddleware';
import HashProtocol from 'farce/lib/HashProtocol';
import MemoryProtocol from 'farce/lib/MemoryProtocol';
import { Resolver } from 'found-relay';
import { ScrollManager } from 'found-scroll';
import createInitialFarceRouter from 'found/lib/createInitialFarceRouter';
import createRender from 'found/lib/createRender';
import ReactDOMServer from 'react-dom/server';
import serialize from 'serialize-javascript';
import { ServerStyleSheet } from 'styled-components';
import { getFarceResult } from 'found/lib/server';
export { Link } from 'found';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

// A type of promise-like that resolves synchronously and supports only one observer
var _iteratorSymbol =
/*#__PURE__*/
typeof Symbol !== "undefined" ? Symbol.iterator || (Symbol.iterator =
/*#__PURE__*/
Symbol("Symbol.iterator")) : "@@iterator"; // Asynchronously iterate through an object's values
var _asyncIteratorSymbol =
/*#__PURE__*/
typeof Symbol !== "undefined" ? Symbol.asyncIterator || (Symbol.asyncIterator =
/*#__PURE__*/
Symbol("Symbol.asyncIterator")) : "@@asyncIterator"; // Asynchronously iterate on a value using it's async iterator if present, or its synchronous iterator if missing

function _catch(body, recover) {
  try {
    var result = body();
  } catch (e) {
    return recover(e);
  }

  if (result && result.then) {
    return result.then(void 0, recover);
  }

  return result;
} // Asynchronously await a promise and pass the result to a finally continuation

function buildClientApp(config) {
  return new Promise(function (resolve, reject) {
    try {
      var _temp2 = _catch(function () {
        var _config$getFarceConfi = config.getFarceConfig,
            getFarceConfig = _config$getFarceConfi === void 0 ? function () {
          return {};
        } : _config$getFarceConfi,
            getRelayEnvironment = config.getRelayEnvironment,
            _config$initialRoute = config.initialRoute,
            initialRoute = _config$initialRoute === void 0 ? "/" : _config$initialRoute,
            _config$render = config.render,
            render = _config$render === void 0 ? {} : _config$render,
            _config$routes = config.routes,
            routes = _config$routes === void 0 ? [] : _config$routes; // Set default history options

        var history = _extends({
          middleware: [createQueryMiddleware({
            parse: qs.parse,
            stringify: qs.stringify
          })]
        }, config.history);

        var relayEnvironment = getRelayEnvironment();

        var getHistoryProtocol = function getHistoryProtocol() {
          switch (history.protocol) {
            case "browser":
              return new BrowserProtocol();

            case "hash":
              return new HashProtocol();

            case "memory":
              return new MemoryProtocol(initialRoute);

            default:
              return new BrowserProtocol();
          }
        };

        var resolver = new Resolver(relayEnvironment);
        var FoundRender = createRender(render || {});
        return Promise.resolve(createInitialFarceRouter(_extends({
          historyProtocol: getHistoryProtocol(),
          historyMiddlewares: history.middleware,
          historyOptions: history.options,
          routeConfig: routes,
          resolver: resolver,
          render: function render(renderArgs) {
            return React.createElement(ScrollManager, {
              renderArgs: renderArgs,
              relayEnvironment: relayEnvironment
            }, React.createElement(FoundRender, Object.assign({}, renderArgs)));
          }
        }, getFarceConfig()))).then(function (Router) {
          var ClientApp = function ClientApp() {
            var FoundRouter = function FoundRouter() {
              return React.createElement(Router, {
                resolver: resolver
              });
            };

            if (render.renderWrapper) {
              return render.renderWrapper({
                Router: FoundRouter,
                relayEnvironment: relayEnvironment,
                routes: routes
              });
            } else {
              return React.createElement(FoundRouter, null);
            }
          };

          resolve({
            ClientApp: ClientApp
          });
        });
      }, function (error) {
        console.error(error);
        reject(error);
      });

      return Promise.resolve(_temp2 && _temp2.then ? _temp2.then(function () {}) : void 0);
    } catch (e) {
      return Promise.reject(e);
    }
  });
}

function buildServerApp(config) {
  return new Promise(function (resolve, reject) {
    try {
      return Promise.resolve(_catch(function () {
        var _config$routes = config.routes,
            routes = _config$routes === void 0 ? [] : _config$routes,
            _config$getFarceConfi = config.getFarceConfig,
            getFarceConfig = _config$getFarceConfi === void 0 ? function () {
          return {};
        } : _config$getFarceConfi,
            _config$getRelayEnvir = config.getRelayEnvironment,
            getRelayEnvironment = _config$getRelayEnvir === void 0 ? null : _config$getRelayEnvir,
            _config$headTags = config.headTags,
            headTags = _config$headTags === void 0 ? [] : _config$headTags,
            _config$render = config.render,
            render = _config$render === void 0 ? {} : _config$render,
            _config$serializeRela = config.serializeRelayData,
            serializeRelayData = _config$serializeRela === void 0 ? function (data) {
          return serialize(data, {
            isJSON: true
          });
        } : _config$serializeRela,
            _config$url = config.url,
            url = _config$url === void 0 ? "/" : _config$url; // Set default history options

        var history = _extends({
          middleware: [createQueryMiddleware({
            parse: qs.parse,
            stringify: qs.stringify
          })]
        }, config.history);

        var relayEnvironment = getRelayEnvironment();
        var resolver = new Resolver(relayEnvironment);
        var foundRender = createRender(render || {});
        return Promise.resolve(getFarceResult(_extends({
          url: url,
          historyMiddlewares: history.middleware,
          routeConfig: routes,
          resolver: resolver,
          render: foundRender
        }, getFarceConfig()))).then(function (_ref) {
          var redirect = _ref.redirect,
              status = _ref.status,
              element = _ref.element;

          function _temp2() {
            // Clean relay response and remove unneeded data structures
            relayData = cleanRelayData(relayData); // Build up script tags to inject into head

            var scripts = ["\n        <script>\n          var __RELAY_BOOTSTRAP__ = " + serializeRelayData(relayData) + ";\n        </script>\n      "].join("\n"); // Extract CSS styleTags to inject for SSR pass

            var styleTags = sheet.getStyleTags();
            var result = {
              ServerApp: bodyHTML,
              bodyHTML: bodyHTML,
              headTags: headTags,
              scripts: scripts,
              status: status,
              styleTags: styleTags
            }; // Only exporting this for testing purposes, don't go around using this
            // elsewhere, we’re serious.

            if (typeof jest !== "undefined") {
              Object.defineProperty(result, __SERVER_APP_TEST__, {
                value: ServerApp
              });
            }

            resolve(result);
          }

          if (redirect) {
            resolve({
              redirect: redirect
            });
            return;
          }

          var ServerApp = function ServerApp() {
            if (render.renderWrapper) {
              return render.renderWrapper({
                Router: function Router() {
                  return element;
                },
                relayEnvironment: relayEnvironment,
                routes: routes
              });
            } else {
              return element;
            }
          }; // Render component tree and collect styles


          var sheet = new ServerStyleSheet();
          var bodyHTML = ReactDOMServer.renderToString(sheet.collectStyles(React.createElement(ServerApp, null))); // Get cache from middleware

          var relayData = {};

          var _temp = function () {
            if (relayEnvironment.relaySSRMiddleware) {
              return Promise.resolve(relayEnvironment.relaySSRMiddleware.getCache()).then(function (_relayEnvironment$rel) {
                relayData = _relayEnvironment$rel;
              });
            }
          }();

          return _temp && _temp.then ? _temp.then(_temp2) : _temp2(_temp);
        });
      }, function (error) {
        console.error(error);
        reject(error);
      }));
    } catch (e) {
      return Promise.reject(e);
    }
  });
}
/**
 * Cleans relay response of unneeded data structures
 */

function cleanRelayData(relayData) {
  try {
    relayData.forEach(function (item) {
      item.forEach(function (i) {
        delete i._res;
      });
    });
  } catch (error) {
    console.error("[@artsy/arc] Error cleaning data", error);
  }

  return relayData;
}

var __SERVER_APP_TEST__ = typeof jest !== "undefined" ?
/*#__PURE__*/
Symbol() : null;

export { __SERVER_APP_TEST__, buildClientApp, buildServerApp };
//# sourceMappingURL=arc.esm.js.map
